// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useDialogControl } from '@hooks/other/useDialogControl';
import { useGetTransferRows } from '@hooks/queries/registry/transfer/useGetTransferRows';
import React from 'react';
import { CircularProgress, Stack, Typography, Tabs, Tab } from '@mui/material';
import { GridRowParams } from '@mui/x-data-grid';
import { ChoiceDialog } from '../../../components/ChoiceDialog';
import { DataTable } from '../../../components/Table/DataTable';
import { Details } from '../../../components/Table/Details';
import { createColumns } from '../../../components/Table/createColumns';
import { RowNew } from '../../../components/Table/types';
import { useCreateChoices } from '../../../components/Table/useCreateChoices';
import { useModuleParty } from '../../../contexts/ModuleContext';
import useIsPackageAvailableFrom from '../../../hooks/queries/settings/useIsPackageAvailableFrom';
import { useOperatorParty } from '../../../hooks/queries/utility-operator/useOperatorParty';
import { PageWrapper } from '../../PageWrapper';
import { TransfersPreapprovals } from '../transferPreapproval/TransfersPreapprovals';
import { OfferTransferDialog } from './OfferTransferDialog';

interface TransfersPageProps {
  disableVirtualization?: boolean;
}
export const TransfersPage: React.FC<TransfersPageProps> = ({ disableVirtualization }) => {
  const [tab, setTab] = React.useState('1');
  const [selectedRow, setSelectedRow] = React.useState<RowNew | undefined>();
  const { dialogName, setDialogName, onCloseDialog } = useDialogControl();

  // Transfer offers
  const transferRows = useGetTransferRows();
  const columns = createColumns();
  const onClose = () => setSelectedRow(undefined);
  const actions = useCreateChoices(onClose, selectedRow);
  const offerTransferButton = [
    {
      name: 'Transfer',
      onClick: () => setDialogName('transfer'),
    },
  ];

  const operator = useOperatorParty()?.data || '';
  const party = useModuleParty();

  // Feature flag for preapprovals
  const usePreapprovals = useIsPackageAvailableFrom(
    'utility-registry-app-v0',
    [operator, party],
    '0.6.0',
  );

  const isLoading = transferRows.isLoading || usePreapprovals.isLoading;

  const pageName = (
    <Stack direction="row" sx={{ display: 'flex', flex: 1 }}>
      <Typography variant="h4" color="primary">
        Transfers
      </Typography>

      {isLoading && <CircularProgress sx={{ ml: 2, mt: 0.5 }} size={20} />}
    </Stack>
  );

  const onRowClick = (r: GridRowParams<RowNew>) => {
    setSelectedRow(r.row);
  };

  const [preapprovalDialogOpen, setPreapprovalDialogOpen] = React.useState(false);

  const preapproveButton = [
    {
      name: 'Preapprove',
      onClick: () => setPreapprovalDialogOpen(true),
    },
  ];

  // Determine which buttons to show based on active tab
  const buttons = tab === '1' ? offerTransferButton : preapproveButton;

  return (
    <PageWrapper buttons={buttons} pageName={pageName}>
      <Tabs value={tab} onChange={(_, v) => setTab(v as string)}>
        <Tab sx={{ textTransform: 'capitalize' }} value="1" label="offers" />
        {usePreapprovals.data && (
          <Tab sx={{ textTransform: 'capitalize' }} value="2" label="preapprovals" />
        )}
      </Tabs>
      {tab === '1' && (
        <>
          <ChoiceDialog
            title="Transfer details"
            open={!!selectedRow}
            onClose={onClose}
            actions={actions}
          >
            {selectedRow && <Details row={selectedRow} />}
          </ChoiceDialog>
          {dialogName === 'transfer' && <OfferTransferDialog open onClose={onCloseDialog} />}
          <DataTable
            disableVirtualization={disableVirtualization}
            rows={transferRows.rows}
            rowKey={(a) => a.contractId}
            columns={columns}
            onRowClick={onRowClick}
          />
        </>
      )}
      {tab === '2' && usePreapprovals.data && (
        <TransfersPreapprovals
          disableVirtualization={disableVirtualization}
          dialogOpen={preapprovalDialogOpen}
          setDialogOpen={setPreapprovalDialogOpen}
        />
      )}
    </PageWrapper>
  );
};
